import { css } from '@emotion/css';
import { computed } from 'vue';
import { useTheme } from '../../composables/theme';

export const useStyles = () => {
  const theme = useTheme();
  const disabledColor = theme.value.colors.gray.light1;
  const checkSVG = (stroke: string, size: 'small' | 'large') => {
    switch (size) {
      case 'large':
        return encodeURIComponent(`<svg width="10" height="9.5" viewBox="0 0 10 9.5" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0.833344 3.79167L3.75001 6.70834L9.16668 1.29167" stroke="${stroke}" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`);
      case 'small':
        return encodeURIComponent(`<svg width="10" height="8.25" viewBox="0 0 10 8.25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.6 2.82999L3.98 5.20999L8.4 0.789993" stroke="${stroke}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`);
      default:
        return '';
    }
  };

  const baseClass = computed(() => css({
    '&, *': {
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      color: theme.value.typography.color,
      fontFamily: theme.value.typography.fontFamily,
      fontWeight: '400',
      fontStyle: 'normal',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
    },
    '&__wrapper': {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '0.1875rem',
    },
    '&__input': {
      appearance: 'none',
      WebkitAppearance: 'none',
      font: 'inherit',
      color: theme.value.colors.primary.main,
      border: `1px solid ${theme.value.colors.primary.main}`,
      borderRadius: '0.125rem',
      display: 'grid',
      placeContent: 'center',
      cursor: 'pointer',
      transform: 'translateY(-0.5px)',
      '&:hover:enabled': {
        backgroundColor: theme.value.colors.primary.light3,
      },
      '&:focus': {
        border: `1px solid ${theme.value.colors.primary.light1}`,
      },
      '&::before': {
        display: 'grid',
        placeContent: 'center',
        borderRadius: '0.125rem',
        transform: 'scale(0)',
        transition: '120ms transform ease-in-out',
        backgroundColor: 'CanvasText',
      },
      '&:checked::before': {
        transform: 'scale(1)',
      },
      '&:checked:disabled::before': {
        boxShadow: 'none',
        backgroundColor: 'transparent',
      },
      '&:disabled': {
        color: disabledColor,
        border: `1px solid ${disabledColor}`,
        cursor: 'not-allowed',
      },
    },
    '&--disabled': {
      color: disabledColor,
      cursor: 'not-allowed',
    },
    '&--large': {
      lineHeight: '1.375rem',
      fontSize: '1rem',
      '&__input': {
        width: '1.125rem',
        height: '1.125rem',
        '&::before': {
          boxShadow: `inset 0 0 0 1.125rem ${theme.value.colors.primary.main}`,
          content: `url('data:image/svg+xml,${checkSVG('white', 'large')}')`,
          width: '1.125rem',
          height: '1.125rem',
        },
        '&:checked:disabled::before': {
          content: `url('data:image/svg+xml,${checkSVG(
            disabledColor,
            'large',
          )}')`,
        },
      },
    },
    '&--small': {
      lineHeight: '1.25rem',
      fontSize: '0.875rem',
      '&__input': {
        width: '1rem',
        height: '1rem',
        '&::before': {
          boxShadow: `inset 0 0 0 1rem ${theme.value.colors.primary.main}`,
          content: `url('data:image/svg+xml,${checkSVG('white', 'small')}')`,
          width: '1rem',
          height: '1rem',
        },
        '&:checked:disabled::before': {
          content: `url('data:image/svg+xml,${checkSVG(
            disabledColor,
            'small',
          )}')`,
        },
      },
    },
  }));
  return baseClass;
};
